import React, { useEffect, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiEyeOutline } from "@mdi/js";
import { Chip, SearchField, Table, cn } from "@heroui/react";
import { clientDetailStore } from "@/stores/ClientDetailStore";
import {
  TRANSACTION_TYPE_LABEL,
  TRANSACTION_TYPE_COLOR,
  TRANSACTION_STATUS_LABEL,
  TRANSACTION_STATUS_COLOR,
  PAYMENT_METHOD_LABEL,
} from "../../ContractorDetailPage/constants";

import {
  SortPanel,
  SortableColumnHeader,
} from "../../ContractorDetailPage/components/shared";
import { CombinedFilter } from "@/shared/ui/CombinedFilter";
import { LoadMoreSentinel } from "@/shared/ui/LoadMoreSentinel";
import { ChipWithTooltip } from "@/shared/ui/ChipWithTooltip";
import { useSortPanel } from "@/hooks/useSortPanel";
import { formatMoneyToRub } from "@shared-utils/helpers";

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const TX_SORT_COLUMNS = [
  { id: "date", label: "Дата" },
  { id: "amount", label: "Сумма" },
];

const TransactionsTab: React.FC = observer(() => {
  const store = clientDetailStore;
  const sort = useSortPanel("date");

  const handleLoadMore = useCallback(() => {
    store.tx.loadMore();
  }, [store]);

  useEffect(() => {
    store.tx.load();
  }, []);

  // Клиентская сортировка
  const sorted = [...store.tx.items].sort((a, b) => {
    let cmp = 0;
    if (sort.sortDescriptor.column === "date") {
      cmp = a.date.localeCompare(b.date);
    } else if (sort.sortDescriptor.column === "amount") {
      cmp = a.amount - b.amount;
    }
    return sort.sortDescriptor.direction === "descending" ? -cmp : cmp;
  });

  const searchTimer = useRef<number>(0);

  const handleSearch = (val: string) => {
    store.tx.setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => store.tx.load(1), 500);
  };

  const handleTypeFilter = (val: string[]) => {
    store.tx.setTypeFilter(val);
    store.tx.load(1);
  };

  const handleStatusFilter = (val: string[]) => {
    store.tx.setStatusFilter(val);
    store.tx.load(1);
  };

  return (
    <div className="cdp-tab-content">
      <div className="cdp-toolbar">
        <div className="cdp-toolbar__left">
          <SearchField
            value={store.tx.search}
            onChange={handleSearch}
            aria-label="Поиск операций"
            className="cdp-search"
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="Поиск..." />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>

          <CombinedFilter
            groups={[
              {
                id: "type",
                label: "Тип",
                options: [
                  { id: "payment", label: "Оплата" },
                  { id: "refund", label: "Возврат" },
                ],
                value: store.tx.typeFilter,
                onChange: handleTypeFilter,
              },
              {
                id: "status",
                label: "Статус",
                options: [
                  { id: "success", label: "Проведён" },
                  { id: "pending", label: "Ожидание" },
                  { id: "processing", label: "Обработка" },
                  { id: "refunded", label: "Возвращён" },
                  { id: "partially_refunded", label: "Частичный возврат" },
                  { id: "failed", label: "Ошибка" },
                ],
                value: store.tx.statusFilter,
                onChange: handleStatusFilter,
              },
            ]}
          />
        </div>
        <div className="cdp-toolbar__right">
          <SortPanel
            columns={TX_SORT_COLUMNS}
            sortDescriptor={sort.sortDescriptor}
            setSortDescriptor={sort.setSortDescriptor}
            isSortOpen={sort.isSortOpen}
            setIsSortOpen={sort.setIsSortOpen}
            sortAnchorRef={sort.sortAnchorRef}
            sortPanelRef={sort.sortPanelRef}
          />
        </div>
      </div>

      <div className="cdp-table-wrap">
        <Table variant="secondary">
          <Table.ScrollContainer className="cdp-scroll-container">
            <Table.Content
              aria-label="История операций"
              className="cdp-table cdp-table--client-tx"
              sortDescriptor={sort.sortDescriptor}
              onSortChange={(d) =>
                sort.setSortDescriptor({
                  column: String(d.column),
                  direction: d.direction as "ascending" | "descending",
                })
              }
            >
              <Table.Header className="cdp-table__head cdp-table__head--sticky">
                <Table.Column
                  id="date"
                  className="cdp-table__th"
                  isRowHeader
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Дата
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="type" className="cdp-table__th">
                  Тип
                </Table.Column>
                <Table.Column id="description" className="cdp-table__th">
                  Описание
                </Table.Column>
                <Table.Column id="sc" className="cdp-table__th">
                  СТО
                </Table.Column>
                <Table.Column id="method" className="cdp-table__th">
                  Метод
                </Table.Column>
                <Table.Column
                  id="amount"
                  className="cdp-table__th cdp-table__th_right"
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Сумма
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="status" className="cdp-table__th">
                  Статус
                </Table.Column>
                <Table.Column
                  id="actions"
                  className="cdp-table__th cdp-table__th--actions"
                >
                  <span className="sr-only">Действия</span>
                </Table.Column>
              </Table.Header>
              <Table.Body className="cdp-table__body">
                {sorted.length === 0 ? (
                  <Table.Row className="cdp-table__row">
                    <Table.Cell className="cdp-table__td" colSpan={8}>
                      <div className="cdp-table-empty">
                        {store.tx.isLoading ? "Загрузка..." : "Нет данных"}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  sorted.map((t) => (
                    <Table.Row
                      key={t.uuid}
                      id={t.uuid}
                      data-uuid={t.uuid}
                      className="cdp-table__row"
                    >
                      <Table.Cell className="cdp-table__td cdp-table__td--date">
                        {fmtDate(t.date)}
                      </Table.Cell>
                      <Table.Cell className="cdp-table__td">
                        <Chip
                          color={TRANSACTION_TYPE_COLOR[t.type] ?? "default"}
                          variant="soft"
                          size="sm"
                        >
                          {TRANSACTION_TYPE_LABEL[t.type] ?? t.type}
                        </Chip>
                      </Table.Cell>
                      <Table.Cell className="cdp-table__td cdp-table__td--wrap">
                        {t.description}
                      </Table.Cell>
                      <Table.Cell className="cdp-table__td cdp-table__td--wrap">
                        {t.serviceCenterName ?? "—"}
                      </Table.Cell>
                      <Table.Cell className="cdp-table__td">
                        {t.paymentMethod
                          ? (PAYMENT_METHOD_LABEL[t.paymentMethod] ??
                            t.paymentMethod)
                          : "—"}
                      </Table.Cell>
                      <Table.Cell
                        className={cn(
                          "cdp-table__td cdp-table__td_right",
                          t.amount >= 0
                            ? "cdp-amount--positive"
                            : "cdp-amount--negative",
                        )}
                      >
                        {t.amount >= 0 ? "+" : ""}
                        {formatMoneyToRub(t.amount)}
                      </Table.Cell>
                      <Table.Cell className="cdp-table__td">
                        {(() => {
                          const statusLabel =
                            t.statusDisplay ||
                            TRANSACTION_STATUS_LABEL[t.status] ||
                            t.status;
                          return (
                            <ChipWithTooltip
                              label={statusLabel}
                              color={
                                TRANSACTION_STATUS_COLOR[t.status] ?? "default"
                              }
                            />
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell className="cdp-table__td cdp-table__td--actions">
                        <button
                          type="button"
                          className="cdp-eye-btn"
                          aria-label="Просмотр"
                          onClick={() =>
                            store.pd.open(t.uuid, t.description, t.type)
                          }
                        >
                          <Icon path={mdiEyeOutline} size={0.8} />
                        </button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
        <LoadMoreSentinel
          onLoadMore={handleLoadMore}
          isLoading={store.tx.isLoading}
          hasMore={store.tx.meta.hasNextPage}
        />
      </div>
    </div>
  );
});

TransactionsTab.displayName = "TransactionsTab";
export default TransactionsTab;
